import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Divider, Modal, notification, Spin } from 'antd';
import { CloseOutlined, LoadingOutlined, PlusCircleOutlined, SaveOutlined, SyncOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { AgGridReact } from 'ag-grid-react';
import { CellValueChangedEvent, ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { PartnerIdentity } from './ObtPartnerDetailsTab';
import { ObtChannelFormat, ObtPartnerChannel } from '../../../__generated__/types';
import client from '../../../config/GraphQLApolloClient';
import { QUERY_REFRESH_PARTNER_CHANNEL, QUERY_UPDATE_CHANNEL, GET_CHANNEL_DATA_BY_PARTNER_DETAILS } from '../obtQueries';
import {
    buildChannelColumns, buildNewChannelRow, channelHasDuplicate,
    prepareChannelForSave,
} from './obtChannelUtils';
import ObtCancelChannelModal from './ObtCancelChannelModal';

// ─── Types ──────────────────────────────────────────────────────────────────────
type ObtChannelTabProps = {
    partnerIdentity: PartnerIdentity;
    partnerChannel: ObtPartnerChannel[];
    idCompany?: number;
    canUpdate: boolean;
};

// ─── Component ──────────────────────────────────────────────────────────────────
const ObtChannelTab: React.FC<ObtChannelTabProps> = ({
    partnerIdentity, partnerChannel, idCompany, canUpdate,
}) => {
    const intl = useIntl();
    const gridApiRef = useRef<GridApi | null>(null);

    const [channelLoading, setChannelLoading] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [rowData, setRowData] = useState<ObtPartnerChannel[]>(partnerChannel ?? []);
    const [isDirty, setIsDirty] = useState(false);
    const [selectedChannel, setSelectedChannel] = useState<ObtPartnerChannel | null>(null);
    const [channelFormat, setChannelFormat] = useState<ObtChannelFormat[]>([]);
    const [channelFormatLoading, setChannelFormatLoading] = useState(false);

    // ─── Load channel format data ──────────────────────────────────────────────
    useEffect(() => {
        if (!partnerIdentity.category || !partnerIdentity.systemCode || !partnerIdentity.companycode) return;
        setChannelFormatLoading(true);
        client.query({
            query: GET_CHANNEL_DATA_BY_PARTNER_DETAILS,
            variables: { category: partnerIdentity.category, system: partnerIdentity.systemCode, companyCode: partnerIdentity.companycode },
            fetchPolicy: 'network-only',
        })
            .then((res) => setChannelFormat(res?.data?.obtGetObtChannelData ?? []))
            .catch((err) => console.error('Failed to load channel format data:', err))
            .finally(() => setChannelFormatLoading(false));
    }, [partnerIdentity.category, partnerIdentity.systemCode, partnerIdentity.companycode]);

    const isEditable = canUpdate && channelFormat.length > 0;
    const columnDefs = useMemo(() => buildChannelColumns(intl, channelFormat, isEditable), [intl, channelFormat, isEditable]);

    const defaultColDef = useMemo<ColDef>(() => ({
        sortable: true, filter: true, resizable: true, editable: false, flex: 1, minWidth: 100, cellEditorPopup: true,
    }), []);

    const onGridReady = useCallback((params: GridReadyEvent) => {
        gridApiRef.current = params.api;
        params.api.sizeColumnsToFit();
    }, []);

    const onCellValueChanged = useCallback((event: CellValueChangedEvent) => {
        setIsDirty(true);
        const field = event.column.getColId();
        const data = event.data;
        if (field === 'invprocess') {
            data.direction = ''; data.provider = ''; data.format = ''; data.channeltype = '';
            gridApiRef.current?.applyTransaction({ update: [data] });
        } else if (field === 'direction') {
            data.provider = ''; data.format = ''; data.channeltype = '';
            gridApiRef.current?.applyTransaction({ update: [data] });
        } else if (field === 'provider') {
            data.format = ''; data.channeltype = '';
            gridApiRef.current?.applyTransaction({ update: [data] });
        } else if (field === 'format') {
            data.channeltype = '';
            gridApiRef.current?.applyTransaction({ update: [data] });
        }
    }, []);

    // ─── Refresh ────────────────────────────────────────────────────────────────
    const refreshChannels = useCallback(async () => {
        setChannelLoading(true);
        try {
            const response = await client.query({
                query: QUERY_REFRESH_PARTNER_CHANNEL,
                variables: {
                    filters: {
                        partnerno: partnerIdentity.partnerno, idpartner: partnerIdentity.idpartner,
                        companycode: [partnerIdentity.companycode], idCompany: idCompany ?? undefined,
                        sysid: [partnerIdentity.systemCode], limit: 0, offset: 0, pageNo: 0, pageSize: 0,
                    },
                },
                fetchPolicy: 'no-cache',
            });
            setRowData(response.data?.obtRefreshPartnerChannelsDetails ?? []);
            setIsDirty(false);
        } catch (err) {
            console.error('Failed to refresh channels:', err);
            notification.error({ message: intl.formatMessage({ id: 'obt.channels.refresh.error', defaultMessage: 'Failed to refresh channels.' }) });
        } finally {
            setChannelLoading(false);
        }
    }, [partnerIdentity, idCompany, intl]);

    // ─── New Row ────────────────────────────────────────────────────────────────
    const onNewRow = useCallback(() => {
        gridApiRef.current?.applyTransaction({ add: [buildNewChannelRow(partnerIdentity)], addIndex: 0 });
        setIsDirty(true);
    }, [partnerIdentity]);

    // ─── Save ───────────────────────────────────────────────────────────────────
    const showSaveConfirmModal = useCallback(() => {
        const selectedRows = gridApiRef.current?.getSelectedRows() ?? [];
        if (selectedRows.length === 0) {
            notification.warning({
                message: intl.formatMessage({ id: 'obt.channels.noRowSelected', defaultMessage: 'No row selected.' }),
                description: intl.formatMessage({ id: 'obt.channels.noRowSelected.desc', defaultMessage: 'Please select a row to update and try again.' }),
            });
            return;
        }
        setShowSaveModal(true);
    }, [intl]);

    const handleSaveOk = useCallback(async () => {
        setShowSaveModal(false);
        const selectedRows = gridApiRef.current?.getSelectedRows() ?? [];
        if (selectedRows.length === 0) return;
        const allRows: ObtPartnerChannel[] = [];
        gridApiRef.current?.forEachNode((node) => { if (node.data) allRows.push(node.data); });
        if (channelHasDuplicate(allRows, selectedRows[0])) {
            notification.error({
                message: intl.formatMessage({ id: 'obt.channels.duplicate.error', defaultMessage: 'Duplicate channel process and overlapping dates.' }),
                description: intl.formatMessage({ id: 'obt.channels.duplicate.error.desc', defaultMessage: 'Please adjust dates of existing channel for this process.' }),
            });
            return;
        }
        const channelModel = prepareChannelForSave(selectedRows[0], partnerIdentity.idpartner);
        setChannelLoading(true);
        try {
            await client.mutate({ mutation: QUERY_UPDATE_CHANNEL, variables: { channelModel }, fetchPolicy: 'no-cache' });
            notification.success({ message: intl.formatMessage({ id: 'obt.channels.save.success', defaultMessage: 'Channel updated successfully.' }) });
            setIsDirty(false);
            await refreshChannels();
        } catch (err) {
            console.error('Failed to save channel:', err);
            notification.error({ message: intl.formatMessage({ id: 'obt.channels.save.error', defaultMessage: 'Failed to save channel.' }) });
        } finally {
            setChannelLoading(false);
        }
    }, [partnerIdentity, intl, refreshChannels]);

    // ─── Cancel Channel ─────────────────────────────────────────────────────────
    const showCancelChannelModal = useCallback(() => {
        const selectedRows = gridApiRef.current?.getSelectedRows() ?? [];
        if (selectedRows.length === 0) {
            notification.error({
                message: intl.formatMessage({ id: 'obt.channels.cancel.selectFirst', defaultMessage: 'No channel selected.' }),
                description: intl.formatMessage({ id: 'obt.channels.cancel.selectFirst.desc', defaultMessage: 'Please select a channel to cancel.' }),
            });
            return;
        }
        setSelectedChannel(selectedRows[0]);
        setShowCancelModal(true);
    }, [intl]);

    const handleCancelSuccess = useCallback(() => {
        setShowCancelModal(false);
        setSelectedChannel(null);
        refreshChannels();
    }, [refreshChannels]);

    return (
        <Spin spinning={channelLoading || channelFormatLoading} size="large" indicator={<LoadingOutlined style={{ fontSize: 24 }} />}>
            <Card size="small" title={intl.formatMessage({ id: 'obt.channels.card.title', defaultMessage: 'Channels' })}>
                <div style={{ marginBottom: 12 }}>
                    <Button icon={<PlusCircleOutlined />} type="primary" onClick={onNewRow} disabled={!canUpdate || channelFormat.length === 0}>
                        {intl.formatMessage({ id: 'obt.channels.btn.new', defaultMessage: 'New' })}
                    </Button>
                    <Divider type="vertical" />
                    <Button icon={<SaveOutlined />} type="primary" onClick={showSaveConfirmModal} disabled={!canUpdate || !isDirty}>
                        {intl.formatMessage({ id: 'obt.channels.btn.save', defaultMessage: 'Save' })}
                    </Button>
                    <Divider type="vertical" />
                    <Button icon={<SyncOutlined />} type="primary" onClick={refreshChannels}>
                        {intl.formatMessage({ id: 'obt.channels.btn.refresh', defaultMessage: 'Refresh' })}
                    </Button>
                    <Divider type="vertical" />
                    <Button icon={<CloseOutlined />} type="primary" danger={true} onClick={showCancelChannelModal} disabled={!canUpdate}>
                        {intl.formatMessage({ id: 'obt.channels.btn.cancel', defaultMessage: 'Cancel' })}
                    </Button>
                </div>
                <Modal key="save-channel" title={intl.formatMessage({ id: 'obt.channels.saveModal.title', defaultMessage: 'Save Channel' })}
                    open={showSaveModal} onOk={handleSaveOk} onCancel={() => setShowSaveModal(false)}
                    okText={intl.formatMessage({ id: 'obt.popconfirm.yes', defaultMessage: 'Yes' })}
                    cancelText={intl.formatMessage({ id: 'obt.popconfirm.no', defaultMessage: 'No' })}>
                    <p>{intl.formatMessage({ id: 'obt.channels.save.confirm', defaultMessage: 'Save channel changes?' })}</p>
                </Modal>
                <ObtCancelChannelModal
                    open={showCancelModal} selectedChannel={selectedChannel}
                    onCancel={() => { setShowCancelModal(false); setSelectedChannel(null); }}
                    onSuccess={handleCancelSuccess}
                />
                <div className="ag-theme-balham" style={{ height: 500, width: '100%' }}>
                    <AgGridReact
                        rowData={rowData} columnDefs={columnDefs} defaultColDef={defaultColDef}
                        onGridReady={onGridReady} onCellValueChanged={onCellValueChanged}
                        rowSelection="single" pagination={true} paginationAutoPageSize={true}
                        animateRows={true} stopEditingWhenCellsLoseFocus={true} singleClickEdit={true}
                    />
                </div>
            </Card>
        </Spin>
    );
};

export default ObtChannelTab;
